/**
 * Configuration Utility
 * Purpose: Centralized configuration management for the application
 * Author: Traklet App
 * Created: 2025
 */

/**
 * Get the API base URL based on environment
 * @returns {string} The API base URL
 */
export const getApiUrl = (): string => {
  // Check if we're in production (digivyn.com domain)
  const isProduction = window.location.hostname.includes('digivyn.com');
  
  if (isProduction) {
    // New clean structure: API endpoints under /api
    return `${window.location.protocol}//${window.location.host}/api`;
  }
  
  // Development environment
  return import.meta.env.VITE_API_URL || 'http://localhost/tinyURL-backend';
};

/**
 * Get the frontend base URL
 * @returns {string} The frontend base URL
 */
export const getFrontendUrl = (): string => {
  const isProduction = window.location.hostname.includes('digivyn.com');
  
  if (isProduction) {
    // New clean structure: frontend at root of traklet.digivyn.com
    return `${window.location.protocol}//${window.location.host}`;
  }
  
  return `http://localhost:${import.meta.env.VITE_PORT || 3002}`;
};

/**
 * Environment configuration
 */
export const config = {
  api: {
    baseUrl: getApiUrl(),
    timeout: 10000,
  },
  frontend: {
    baseUrl: getFrontendUrl(),
  },
  environment: {
    isDevelopment: !window.location.hostname.includes('digivyn.com'),
    isProduction: window.location.hostname.includes('digivyn.com'),
  },
  logging: {
    enabled: true,
    level: 'info', // 'debug', 'info', 'warn', 'error'
  },
};

/**
 * Logger utility for centralized logging
 */
export const logger = {
  debug: (message: string, ...args: any[]) => {
    if (config.logging.enabled && ['debug'].includes(config.logging.level)) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  },
  info: (message: string, ...args: any[]) => {
    if (config.logging.enabled && ['debug', 'info'].includes(config.logging.level)) {
      console.info(`[INFO] ${message}`, ...args);
    }
  },
  warn: (message: string, ...args: any[]) => {
    if (config.logging.enabled && ['debug', 'info', 'warn'].includes(config.logging.level)) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },
  error: (message: string, ...args: any[]) => {
    if (config.logging.enabled) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  },
};

export default config;